import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRightLeft,
  CalendarDays,
  CalendarPlus,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  Shuffle,
} from "lucide-react";
import {
  AppButton,
  AppCard,
  AppCardContent,
  AppDataTable,
  Callout,
  EmptyState,
  IconButton,
  ProgressBar,
  SegmentedControl,
  StatusBadge,
} from "../../ui";
import apiClient from "../../../utils/apiClient";
import RescheduleDialog from "./RescheduleDialog";
import ScheduleNotice from "./ScheduleNotice";
import ScheduleChangeLog from "./ScheduleChangeLog";
import CarryOverDialog from "./CarryOverDialog";
import RepackDialog from "./RepackDialog";
import GeneratePlanDialog from "./GeneratePlanDialog";
import {
  conflictText,
  dayKey,
  formatDay,
  formatTaughtDay,
  isReschedulable,
  labelOf,
  movedFrom,
  pluralNoun,
  taughtDayKey,
  weekdaysLabel,
} from "./scheduleFormat";

const stateOf = (session) => session.schedule?.state || session.status || "scheduled";
const topicsDone = (session) => (session.topics || []).filter((t) => t.status === "completed").length;
const UNIT = { day: "Day", module: "Class", smart: "Session" };

const FILTERS = {
  all: () => true,
  upcoming: (s) => isReschedulable(s),
  overdue: (s) => !!s.schedule?.overdue,
  completed: (s) => s.status === "completed",
};

/** A class taught (or under way) that left topics unticked — something to carry over. */
const canCarryOver = (s) => s.kind !== "smart"
  && (s.status === "completed" || s.status === "in_progress")
  && (s.topics || []).some((t) => t.status !== "completed");

/** The date cell: the plan, and what is worth knowing about it. */
function DateCell({ session }) {
  const planned = dayKey(session.plannedDate);
  const original = movedFrom(session);
  const taught = session.status === "completed" ? taughtDayKey(session.actualDate) : null;
  const conflict = session.schedule?.conflict;
  return (
    <span className="tw-flex tw-flex-col tw-gap-0.5">
      <span className="tw-whitespace-nowrap tw-text-[0.8125rem] tw-font-medium tw-text-content">
        {planned ? formatDay(planned) : session.taughtBeforePlan ? "Before the plan" : "No date yet"}
      </span>
      {taught && taught !== planned && (
        <span className="tw-whitespace-nowrap tw-text-xs tw-text-content-muted">Taught {formatTaughtDay(session.actualDate)}</span>
      )}
      {!taught && original && (
        <span className="tw-whitespace-nowrap tw-text-xs tw-text-content-muted">Moved from {formatDay(original)}</span>
      )}
      {conflict && (
        <span className="tw-whitespace-nowrap tw-text-xs tw-font-medium tw-text-warning-fg">{conflictText(conflict)}</span>
      )}
    </span>
  );
}

/** Under an expanded row: every topic and whether it was covered, and the trainer's note. */
function ClassDetail({ session }) {
  const topics = session.topics || [];
  return (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-px-5 tw-py-3">
      {session.smart?.description && <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content">{session.smart.description}</p>}
      {topics.length ? (
        <ul className="tw-m-0 tw-grid tw-list-none tw-gap-1.5 tw-p-0 min-[768px]:tw-grid-cols-2">
          {topics.map((topic, i) => {
            const done = topic.status === "completed";
            const Icon = done ? CheckCircle2 : Circle;
            return (
              <li key={i} className="tw-flex tw-items-start tw-gap-2 tw-text-[0.8125rem] tw-text-content">
                <Icon size={15} aria-hidden="true" className={done ? "tw-mt-0.5 tw-shrink-0 tw-text-success" : "tw-mt-0.5 tw-shrink-0 tw-text-content-subtle"} />
                <span>
                  {topic.parts > 1 ? `${topic.name} (part ${topic.part} of ${topic.parts})` : topic.name}
                  {topic.carriedFrom != null && <span className="tw-text-xs tw-text-content-muted"> · carried from class {topic.carriedFrom}</span>}
                  <span className="tw-sr-only">{done ? " — covered" : " — not covered yet"}</span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : !session.smart && (
        <p className="tw-m-0 tw-text-xs tw-text-content-muted">No topics listed.</p>
      )}
      {session.smart && (
        <p className="tw-m-0 tw-text-xs tw-text-content-muted">
          {session.smart.resourceCount
            ? `${session.smart.resourceCount} resource${session.smart.resourceCount === 1 ? "" : "s"} with this session.`
            : "No resources with this session."}
        </p>
      )}
      {session.skipReason && <p className="tw-m-0 tw-text-xs tw-text-content-muted">{session.skipReason}</p>}
      {session.trainerNotes && (
        <p className="tw-m-0 tw-text-xs tw-text-content-muted">
          <span className="tw-font-semibold tw-text-content">Trainer’s note:</span> {session.trainerNotes}
        </p>
      )}
    </div>
  );
}

/** One figure in the summary strip. */
function Figure({ label, value, detail }) {
  return (
    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
      <dt className="tw-text-xs tw-font-medium tw-text-content-muted">{label}</dt>
      <dd className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">{value}</dd>
      {detail && <dd className="tw-m-0 tw-text-xs tw-text-content-subtle">{detail}</dd>}
    </div>
  );
}

/**
 * BatchSyllabusSchedule — a batch's class plan by date, whatever it is made of:
 * the days of a day-wise syllabus, classes formed from a module-wise one, or
 * Smart Learning sessions. What was planned for when, what has been taught,
 * what is overdue, and who moved what.
 *
 * Props:
 *   batchId
 *   canReschedule — move dates, carry topics over, re-pack (the server still decides)
 *   canGenerate   — plan a batch that has no plan yet
 *   canEditBatch  — offer "Edit batch" when the plan runs past the batch's end
 *   onNotify      — (message, type) => void, for the page's toaster
 *   onPlanChange  — (hasPlan) => void, so the page can drop its "no plan" prompt
 */
const BatchSyllabusSchedule = ({
  batchId, canReschedule = false, canGenerate = false, canEditBatch = false, onNotify, onPlanChange,
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(() => new Set());
  const [dialog, setDialog] = useState(null); // { type, session?, date? }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [planRes, changesRes] = await Promise.all([
        apiClient.get(`/syllabus/batch/${batchId}/plan`),
        apiClient.get(`/syllabus/batch/${batchId}/syllabus/changes`).catch(() => null),
      ]);
      setData(planRes.data);
      setChanges(changesRes?.data?.changes || []);
      setLoadError("");
      onPlanChange?.((planRes.data?.sessions || []).length > 0);
    } catch (err) {
      setData(null);
      setLoadError(err?.response?.data?.message || "The plan couldn’t be loaded. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, [batchId, onPlanChange]);

  useEffect(() => {
    load();
  }, [load]);

  const sessions = useMemo(() => data?.sessions || [], [data]);
  const schedule = data?.schedule || null;
  const format = data?.format || "day";
  const unit = UNIT[format] || "Day";
  const noun = unit.toLowerCase();
  const nouns = pluralNoun(format);

  const counts = useMemo(() => {
    const out = {};
    for (const key of Object.keys(FILTERS)) out[key] = sessions.filter(FILTERS[key]).length;
    return out;
  }, [sessions]);
  const rows = useMemo(() => sessions.filter(FILTERS[filter] || FILTERS.all), [sessions, filter]);
  const next = sessions.find(isReschedulable);

  const done = (message) => {
    onNotify?.(message, "success");
    load();
  };
  const toggle = (dayNumber) => setExpanded((prev) => {
    const out = new Set(prev);
    if (out.has(dayNumber)) out.delete(dayNumber);
    else out.add(dayNumber);
    return out;
  });

  const addSmartSessions = async () => {
    setBusy(true);
    try {
      const { data: res } = await apiClient.post(`/syllabus/batch/${batchId}/sync-syllabus`);
      done(res.message || "Sessions added.");
    } catch (err) {
      onNotify?.(err?.response?.data?.message || "The sessions were not added.", "error");
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: "day",
      label: unit,
      width: 110,
      render: (s) => {
        const open = expanded.has(s.dayNumber);
        return (
          <span className="tw-inline-flex tw-items-center tw-gap-1">
            <IconButton
              size="xs"
              icon={open ? ChevronDown : ChevronRight}
              label={open ? `Hide ${labelOf(s)} details` : `Show ${labelOf(s)} details`}
              aria-expanded={open}
              onClick={() => toggle(s.dayNumber)}
            />
            <span className="tw-whitespace-nowrap tw-text-[0.8125rem] tw-font-semibold tw-text-content">{labelOf(s)}</span>
          </span>
        );
      },
    },
    { key: "date", label: "Date", width: 150, render: (s) => <DateCell session={s} /> },
    {
      key: "title",
      label: "What it covers",
      render: (s) => (
        <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
          {s.moduleTitle && (
            <span className="tw-truncate tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">
              {s.moduleNumber ? `Module ${s.moduleNumber} · ` : ""}{s.moduleTitle}
            </span>
          )}
          <span className="tw-text-[0.8125rem] tw-font-medium tw-text-content">{s.title}</span>
          {(s.topics || []).length > 1 && (
            <span className="tw-line-clamp-1 tw-text-xs tw-text-content-muted">
              {(s.topics || []).map((t) => t.name).join(" · ")}
            </span>
          )}
        </span>
      ),
    },
    ...(format === "smart" ? [] : [{
      key: "topics",
      label: "Topics done",
      align: "center",
      width: 110,
      render: (s) => (
        <span className="tw-whitespace-nowrap tw-text-[0.8125rem] tw-text-content">
          {topicsDone(s)}/{(s.topics || []).length}
        </span>
      ),
    }]),
    {
      key: "status",
      label: "Status",
      width: 130,
      render: (s) => <StatusBadge domain="syllabusDay" status={stateOf(s)} />,
    },
    ...(canReschedule
      ? [{
        key: "actions",
        label: "",
        width: 140,
        align: "right",
        render: (s) => {
          if (isReschedulable(s) && s.status !== "in_progress") {
            return <AppButton size="xs" variant="outline" onClick={() => setDialog({ type: "reschedule", session: s })}>Reschedule</AppButton>;
          }
          if (canCarryOver(s)) {
            return (
              <AppButton size="xs" variant="ghost" onClick={() => setDialog({ type: "carry", session: s })}>
                <ArrowRightLeft size={12} aria-hidden="true" /> Carry over
              </AppButton>
            );
          }
          if (isReschedulable(s)) {
            return <AppButton size="xs" variant="outline" onClick={() => setDialog({ type: "reschedule", session: s })}>Reschedule</AppButton>;
          }
          return null;
        },
      }]
      : []),
  ];

  // ── Nothing planned yet ──
  if (!loading && !loadError && sessions.length === 0) {
    const notStarted = data?.batchStatus === "Upcoming";
    const hasTemplate = !!data?.template;
    let action = null;
    let description;
    if (format === "smart") {
      description = notStarted
        ? "Its Smart Learning sessions are planned, with dates, when the batch starts."
        : "This batch has none of the course’s Smart Learning sessions yet.";
      if (!notStarted && canGenerate) {
        action = (
          <AppButton onClick={addSmartSessions} disabled={busy}>
            <CalendarPlus size={15} aria-hidden="true" /> {busy ? "Adding…" : "Add the course’s sessions"}
          </AppButton>
        );
      }
    } else if (!hasTemplate) {
      description = "The course has no syllabus yet. Write it from the course’s Syllabus page; a batch is planned from it.";
    } else if (notStarted) {
      description = "Its classes are planned, with dates, when the batch starts.";
    } else {
      description = "This batch started before its syllabus could plan it. Plan it now — you choose where it picks up.";
      if (canGenerate) {
        action = (
          <AppButton onClick={() => setDialog({ type: "generate" })}>
            <CalendarPlus size={15} aria-hidden="true" /> Plan this batch’s classes
          </AppButton>
        );
      }
    }
    return (
      <>
        <EmptyState icon={CalendarDays} title="No class plan for this batch yet" description={description} action={action} />
        <GeneratePlanDialog
          show={dialog?.type === "generate"}
          onHide={() => setDialog(null)}
          batchId={batchId}
          batchStartedBefore={!notStarted}
          onDone={done}
        />
      </>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {loadError && (
        <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={load}>Try again</AppButton>}>
          {loadError}
        </Callout>
      )}

      {data && (
        <AppCard>
          <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-3">
                <span className="tw-text-sm tw-font-semibold tw-text-content">
                  {data.completedDays} of {data.totalDays} {nouns} taught
                </span>
                <span className="tw-text-sm tw-font-semibold tw-text-content">{data.progressPercent}%</span>
              </div>
              <ProgressBar value={data.progressPercent} color={data.progressPercent === 100 ? "success" : "primary"} />
            </div>
            <dl className="tw-m-0 tw-grid tw-grid-cols-2 tw-gap-4 min-[992px]:tw-grid-cols-4">
              <Figure
                label="Next up"
                value={next ? `${labelOf(next)} · ${next.plannedDate ? formatDay(next.plannedDate) : "no date yet"}` : `All ${nouns} taught`}
                detail={next?.title}
              />
              <Figure
                label="Behind schedule"
                value={schedule ? (schedule.overdueCount ? `${schedule.overdueCount} ${schedule.overdueCount === 1 ? noun : nouns}` : "On track") : "—"}
              />
              <Figure
                label={`Last ${noun}`}
                value={schedule?.lastPlannedDate ? formatDay(schedule.lastPlannedDate, { year: true }) : "—"}
                detail={schedule?.batchEndDate ? `Batch ends ${formatDay(schedule.batchEndDate, { year: true })}` : null}
              />
              <Figure
                label="Class days"
                value={schedule?.meetingDays ? weekdaysLabel(schedule.meetingDays) : "—"}
                detail={data.classMinutes ? `${data.classMinutes}-minute classes · holidays skipped` : "Holidays are skipped"}
              />
            </dl>
          </AppCardContent>
        </AppCard>
      )}

      <ScheduleNotice
        schedule={schedule}
        sessions={sessions}
        onRepair={canReschedule ? (session, date) => setDialog({ type: "reschedule", session, date }) : undefined}
        onEditBatch={canEditBatch ? () => navigate(`/batches/edit/${batchId}`) : undefined}
      />

      <AppDataTable
        fill={false}
        loading={loading}
        columns={columns}
        rows={rows}
        rowKey={(s) => s._id || s.dayNumber}
        renderExpandedRow={(s) => (expanded.has(s.dayNumber) ? <ClassDetail session={s} /> : null)}
        emptyMessage={`No ${nouns} match this view.`}
        toolbar={(
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-px-4 tw-py-2.5">
            <SegmentedControl
              label={`Show ${nouns}`}
              value={filter}
              onChange={setFilter}
              options={[
                { value: "all", label: "All", count: counts.all },
                { value: "upcoming", label: "To teach", count: counts.upcoming },
                { value: "overdue", label: "Overdue", count: counts.overdue },
                { value: "completed", label: "Completed", count: counts.completed },
              ]}
            />
            {canReschedule && (
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                {format === "module" && (
                  <AppButton size="sm" variant="outline" onClick={() => setDialog({ type: "repack" })}>
                    <Shuffle size={14} aria-hidden="true" /> Re-pack classes
                  </AppButton>
                )}
                {next && (
                  <AppButton
                    size="sm"
                    variant="outline"
                    onClick={() => setDialog({
                      type: "reschedule",
                      session: next,
                      date: schedule?.suggestion?.fromDay === next.dayNumber ? schedule.suggestion.date : "",
                    })}
                  >
                    <CalendarDays size={14} aria-hidden="true" /> Reschedule from {labelOf(next)}
                  </AppButton>
                )}
              </div>
            )}
          </div>
        )}
      />

      <ScheduleChangeLog changes={changes} unit={unit} />

      <RescheduleDialog
        show={dialog?.type === "reschedule"}
        onHide={() => setDialog(null)}
        batchId={batchId}
        session={dialog?.type === "reschedule" ? dialog.session : null}
        initialDate={dialog?.date}
        today={schedule?.today}
        canEditBatch={canEditBatch}
        onSaved={done}
      />
      <CarryOverDialog
        show={dialog?.type === "carry"}
        onHide={() => setDialog(null)}
        batchId={batchId}
        session={dialog?.type === "carry" ? dialog.session : null}
        onDone={done}
        onOverrun={format === "module" ? (overrun) => setDialog({ type: "repack", overrun }) : undefined}
      />
      <RepackDialog
        show={dialog?.type === "repack"}
        onHide={() => setDialog(null)}
        batchId={batchId}
        overrun={dialog?.type === "repack" ? dialog.overrun : null}
        onDone={done}
      />
    </div>
  );
};

export default BatchSyllabusSchedule;
